import { useState } from 'react';
import {
  Briefcase, User, Mail, Building2, MapPin, FileText, Link as LinkIcon,
  Loader2, Image as ImageIcon, BadgePoundSterling, Crown, Sparkles, CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';
import { submitPaidJob, createPaidJobCheckout } from '@/lib/api';

const JOB_TYPES = ['Full-time', 'Part-time', 'Contract', 'Temporary', 'Apprenticeship', 'Internship', 'Volunteer'];

const PostJobPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [featureAddon, setFeatureAddon] = useState(false);
  const [formData, setFormData] = useState({
    title: '', employer: '', location: 'Paisley', salary: '', job_type: '',
    description: '', apply_link: '', apply_email: '',
    contact_name: '', contact_email: '', business_name: '', logo_url: '',
  });

  const totalAmount = 15 + (featureAddon ? 15 : 0);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.employer || !formData.location ||
        !formData.contact_name || !formData.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.apply_link && !formData.apply_email) {
      toast.error('Provide an application URL or email so candidates can apply');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...formData, feature_addon: featureAddon };
      const res = await submitPaidJob(payload);
      const paidId = res.data.id;
      const checkoutRes = await createPaidJobCheckout(paidId, window.location.origin);
      window.location.href = checkoutRes.data.url;
    } catch (error) {
      console.error('Post-a-job submission error:', error);
      toast.error(error.response?.data?.detail || 'Submission failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="post-job-page">
      <SEOPage.Register />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-sky-600 via-blue-700 to-indigo-800 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, white 0%, transparent 40%), radial-gradient(circle at 80% 70%, white 0%, transparent 40%)',
          }}
        />
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <Badge className="bg-white/20 text-white border-0 mb-4" data-testid="post-job-badge">
            <Briefcase className="h-3 w-3 mr-1" /> Post a Job
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
            Hire in Paisley &amp; Renfrewshire
          </h1>
          <p className="text-sky-100 text-lg max-w-2xl mx-auto">
            Reach thousands of local candidates. Your listing goes live instantly and stays visible for 30 days.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-white/10 rounded-full px-6 py-3">
            <BadgePoundSterling className="h-6 w-6" />
            <span className="text-3xl font-bold">£15</span>
            <span className="text-sky-100 text-sm">one-off &middot; 30 days live</span>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          {/* Benefits row */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: CheckCircle, title: 'Instant Publishing', desc: 'Your job goes live the moment payment clears — no waiting for approval.' },
              { icon: MapPin, title: 'Local Reach', desc: 'Seen by candidates browsing Paisley & Renfrewshire\'s dedicated Jobs Board.' },
              { icon: Sparkles, title: 'Mirrored on paisley.org.uk', desc: 'Your listing syndicates automatically to our sister site for double the visibility.' },
            ].map((item, i) => (
              <Card key={i} className="text-center border-sky-100" data-testid={`post-job-benefit-${i}`}>
                <CardContent className="p-6">
                  <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <item.icon className="h-5 w-5 text-sky-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-500 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="shadow-lg" data-testid="post-job-form-card">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-sky-500" /> Job Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><FileText className="h-4 w-4 text-slate-400" /> Job Title *</Label>
                    <Input name="title" value={formData.title} onChange={handleChange}
                           placeholder="e.g. Senior Marketing Manager" required
                           data-testid="post-job-title" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><Building2 className="h-4 w-4 text-slate-400" /> Employer *</Label>
                    <Input name="employer" value={formData.employer} onChange={handleChange}
                           placeholder="e.g. Paisley Textiles Ltd" required
                           data-testid="post-job-employer" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><MapPin className="h-4 w-4 text-slate-400" /> Location *</Label>
                    <Input name="location" value={formData.location} onChange={handleChange}
                           placeholder="e.g. Paisley" required
                           data-testid="post-job-location" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><BadgePoundSterling className="h-4 w-4 text-slate-400" /> Salary</Label>
                    <Input name="salary" value={formData.salary} onChange={handleChange}
                           placeholder="e.g. £30,000 - £35,000"
                           data-testid="post-job-salary" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><Briefcase className="h-4 w-4 text-slate-400" /> Job Type</Label>
                    <Select value={formData.job_type} onValueChange={(v) => setFormData(p => ({ ...p, job_type: v }))}>
                      <SelectTrigger data-testid="post-job-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {JOB_TYPES.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><ImageIcon className="h-4 w-4 text-slate-400" /> Company Logo URL</Label>
                    <Input name="logo_url" value={formData.logo_url} onChange={handleChange}
                           placeholder="https://example.com/logo.png"
                           data-testid="post-job-logo-url" />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-1.5"><FileText className="h-4 w-4 text-slate-400" /> Description</Label>
                  <Textarea name="description" value={formData.description} onChange={handleChange}
                            placeholder="Role summary, responsibilities, requirements, perks..." rows={6}
                            data-testid="post-job-desc" />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><LinkIcon className="h-4 w-4 text-slate-400" /> Apply URL</Label>
                    <Input name="apply_link" type="url" value={formData.apply_link} onChange={handleChange}
                           placeholder="https://yourcompany.com/careers/..."
                           data-testid="post-job-apply-link" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><Mail className="h-4 w-4 text-slate-400" /> or Apply Email</Label>
                    <Input name="apply_email" type="email" value={formData.apply_email} onChange={handleChange}
                           placeholder="careers@yourcompany.com"
                           data-testid="post-job-apply-email" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 -mt-2">Provide at least one of the above so candidates can apply.</p>

                <hr />

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><User className="h-4 w-4 text-slate-400" /> Your Name *</Label>
                    <Input name="contact_name" value={formData.contact_name} onChange={handleChange} required
                           data-testid="post-job-contact-name" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><Mail className="h-4 w-4 text-slate-400" /> Your Email * (private)</Label>
                    <Input name="contact_email" type="email" value={formData.contact_email} onChange={handleChange} required
                           data-testid="post-job-contact-email" />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-1.5"><Building2 className="h-4 w-4 text-slate-400" /> Business Name (if different from employer)</Label>
                  <Input name="business_name" value={formData.business_name} onChange={handleChange}
                         data-testid="post-job-business" />
                </div>

                {/* Feature upsell */}
                <div
                  className={`rounded-lg border-2 p-5 transition-all cursor-pointer ${
                    featureAddon
                      ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-md'
                      : 'border-slate-200 bg-white hover:border-amber-200'
                  }`}
                  onClick={() => setFeatureAddon(v => !v)}
                  data-testid="post-job-feature-upsell"
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={featureAddon}
                      onCheckedChange={setFeatureAddon}
                      className="mt-1"
                      data-testid="post-job-feature-checkbox"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Crown className="h-4 w-4 text-amber-500" />
                        <span className="font-semibold text-slate-900">Feature this job (+£15)</span>
                        <Badge className="bg-amber-500 text-white border-0 text-xs">3x more clicks</Badge>
                      </div>
                      <p className="text-sm text-slate-600">
                        Pinned to slot #1 with a gold border and Featured badge for the full 30 days.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total + submit */}
                <div className="flex items-center justify-between bg-slate-100 rounded-lg p-4">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wide">Total</div>
                    <div className="text-3xl font-bold text-slate-900" data-testid="post-job-total-amount">
                      £{totalAmount}
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500 max-w-[200px]">
                    Secure payment via Stripe. Job goes live immediately after payment.
                  </div>
                </div>

                <Button type="submit" disabled={submitting}
                        className="w-full h-12 bg-gradient-to-r from-sky-600 to-indigo-700 hover:from-sky-700 hover:to-indigo-800 text-white text-base shadow-lg"
                        data-testid="post-job-submit-btn">
                  {submitting
                    ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Redirecting to Stripe...</>
                    : <>Pay £{totalAmount} &amp; Publish Job <Briefcase className="h-4 w-4 ml-2" /></>}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PostJobPage;
